/**
 * Runs a fire-and-forget async call so a failed request cannot escape as an
 * unhandled promise rejection.
 *
 * Why this exists: the page-level fetches are started from `useEffect` and
 * from click handlers without `await` or `.catch()`. When a request fails —
 * a network error, a timeout, or a 5xx — the rejection had nowhere to go, so
 * Create React App showed its red "Uncaught runtime errors" overlay on top of
 * an otherwise working page. In production there is no overlay, but the
 * rejection is still unhandled and the loading spinner never clears.
 *
 * The 401 case does not reach here: `axiosInstance`'s response interceptor
 * redirects to /login and returns a promise that never settles, precisely so
 * no caller runs against a page that is already navigating away.
 *
 * @param label   Identifies the call in the console, e.g. "AccountPage.getUserInfo".
 * @param run     The async call to start.
 * @param onError Optional cleanup, typically clearing a loading state.
 */
export const runSafely = (
    label: string,
    run: () => Promise<unknown>,
    onError?: () => void,
): void => {
    run().catch((error) => {
        console.error(`${label} failed:`, error);
        onError?.();
    });
};
